import { useEffect, useRef, type RefObject } from "react";

/**
 * Close a light dismissable surface — a dropdown, a popover, a menu — on **outside pointerdown** or
 * **Escape**.
 *
 * Two details this exists to stop each caller re-deriving:
 * - **`pointerdown`, not `click`.** A click fires after the press completes, so a press that starts
 *   inside the menu and releases outside (a drag, a text selection) would close it; and on touch the
 *   click is delayed. Pointerdown closes when the user actually reaches elsewhere.
 * - **The listener sits on `document` only while open**, so a closed surface costs nothing and there is
 *   no stale handler racing a re-open.
 *
 * This is the LIGHT dismiss contract. A modal or sheet is a different thing — it owns a focus trap, the
 * Escape *stack* and a body-scroll lock — and belongs in `Modal`/`Sheet`/`AcDialog`, never here.
 */
export function useDismiss(
  open: boolean,
  /** Called on outside press or Escape. Need not be referentially stable. */
  onDismiss: () => void,
  /** The surface itself — a press inside it is never a dismissal. Include the trigger, or pressing it re-opens. */
  ref: RefObject<HTMLElement | null>,
): void {
  // Held in a ref so an inline `() => setOpen(false)` does not resubscribe the listeners every render.
  const handler = useRef(onDismiss);
  handler.current = onDismiss;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handler.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handler.current();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, ref]);
}
